import { useTranslation } from "@/i18n/client";
import { Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { Tabs as MantineTabs } from "@mantine/core";
import Tabs from "@/components/Tabs";
import { RadioCheckboxTick } from "@/components/Icons";
import Text from "@/components/Text";
import { InputTooltip } from "@/components/InputTooltip";
import { Checkbox } from "@/components/Controls/Checkbox";
import AddressAutosuggest from "@/screens/common/AddressAutosuggest";
import ForeignAddress from "../../Customer/Address/ForeignAddress";
import { AddressFormValues } from "@/screens/Customer/Address/form";
import { CustomerType } from "@/types/customerType";
import { useContractState } from "@/hooks/useContractState";
import { Process } from "@/types/contract";

interface MainAddressProps {
  form: UseFormReturnType<AddressFormValues>;
  customerType: CustomerType;
  showForeignAddress?: boolean;
  showPostalCheckbox?: boolean;
}

export const MainAddress = ({ form, customerType, showForeignAddress, showPostalCheckbox = true }: MainAddressProps) => {
  const { t } = useTranslation(["address", "hints"]);

  const { contract } = useContractState();
  const isR1Process = contract.process === Process.NEW_POINT;
  const mainAddressTooltip =
    customerType === "household" ? t("hints:customer.addressMain") : isR1Process ? null : t("hints:customer.addressCompany");

  const activeTab = form.values.isForeign ? "foreign" : "CZ";

  return (
    <Tabs
      value={activeTab}
      onChange={value => {
        if (value !== null && value !== activeTab) {
          const isForeign = value !== "CZ";
          form.setFieldValue("isForeign", isForeign);
        }
      }}>
      <MantineTabs.List>
        <MantineTabs.Tab value="CZ" leftSection={<RadioCheckboxTick data-testid="tickCz" fontSize={18} />}>
          {t("czechAddress")}
        </MantineTabs.Tab>
        {showForeignAddress && (
          <MantineTabs.Tab value="foreign" leftSection={<RadioCheckboxTick data-testid="tickForeign" fontSize={18} />}>
            {t("foreignAddress")}
          </MantineTabs.Tab>
        )}
      </MantineTabs.List>
      <MantineTabs.Panel value="foreign">
        <Stack gap={24} mt={24}>
          <ForeignAddress form={form} addressType="MAIN_FOREIGN" />
        </Stack>
      </MantineTabs.Panel>
      <MantineTabs.Panel value="CZ">
        <Stack gap={24} mt={24}>
          <Text variant="reading_small">{t("addressDescription")}</Text>
          <InputTooltip
            showTooltip={!!mainAddressTooltip}
            tooltipProps={{
              label: mainAddressTooltip,
            }}>
            <AddressAutosuggest
              label={customerType === "household" ? t("mainAddress") : t("addressOfCompany")}
              {...form.getInputProps("MAIN")}
            />
          </InputTooltip>
          {showPostalCheckbox && (
            <Checkbox
              label={customerType === "household" ? t("sendLettersToMainAddress") : t("sendLettersToCompanyAddress")}
              size="sm"
              {...form.getInputProps("isSame", { type: "checkbox" })}
            />
          )}
        </Stack>
      </MantineTabs.Panel>
    </Tabs>
  );
};
